import { Controller } from "@hotwired/stimulus"

// TASK-340 (#10): ticks the machine-screen wall clock (HH:MM). Pure atmosphere — no game logic.
// Founder fix 2026-07-09: the value is the machine LOCATION's local time (IANA timezone via the
// `timezone` value — the same location.timezone the demand windows run on), NOT the player's
// device time; blank/invalid timezone falls back to device time. Pauses while the tab is hidden
// (no wasted timers) and resumes on return. Survives summary Turbo Stream morphs via
// data-turbo-permanent. The displayed value only changes on the minute; we tick each second so a
// rollover shows promptly.
export default class extends Controller {
  static targets = [ "hh", "mm", "hourHand", "minuteHand", "accessibleTime" ]
  static values = { timezone: String }

  connect() {
    this.formatter = this.buildFormatter()
    this.slide = this.element.closest(".ve-ds")
    if (this.slide) {
      this.slideObserver = new MutationObserver(() => this.syncTimer())
      this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: [ "inert" ] })
    }
    this.onVisibility = () => this.syncTimer()
    document.addEventListener("visibilitychange", this.onVisibility)
    this.syncTimer()
  }

  disconnect() {
    this.stop()
    this.slideObserver?.disconnect()
    document.removeEventListener("visibilitychange", this.onVisibility)
  }

  start() {
    this.render()
    if (this.timer) return
    this.timer = window.setInterval(() => this.render(), 1000)
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer)
      this.timer = null
    }
  }

  syncTimer() {
    if (!this.slide?.hasAttribute("inert") && !document.hidden) {
      this.start()
    } else {
      this.stop()
    }
  }

  // One cached formatter (built on connect, not per tick). hourCycle h23 keeps midnight "00",
  // never "24". An unknown timezone throws at construction → device-time fallback.
  buildFormatter() {
    const options = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }
    try {
      return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: this.timezoneValue || undefined })
    } catch {
      return new Intl.DateTimeFormat("en-GB", options)
    }
  }

  render() {
    const parts = Object.fromEntries(
      this.formatter.formatToParts(new Date()).map(({ type, value }) => [ type, value ])
    )
    const hh = parts.hour
    const mm = parts.minute
    if (this.hasHhTarget) this.hhTarget.textContent = hh
    if (this.hasMmTarget) this.mmTarget.textContent = mm
    if (this.hasAccessibleTimeTarget) this.accessibleTimeTarget.dateTime = `${hh}:${mm}`

    const hourAngle = ((Number(hh) % 12) * 30) + (Number(mm) * 0.5)
    const minuteAngle = Number(mm) * 6
    if (this.hasHourHandTarget) this.rotateHand(this.hourHandTarget, hourAngle)
    if (this.hasMinuteHandTarget) this.rotateHand(this.minuteHandTarget, minuteAngle)
  }

  rotateHand(target, angle) {
    const x = target.dataset.clockPivotX
    const y = target.dataset.clockPivotY
    target.setAttribute("transform", `rotate(${angle} ${x} ${y})`)
  }
}
